import React from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Button
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
//import BuildIcon from '@mui/icons-material/Build';
import MemoryIcon from '@mui/icons-material/Memory';
import SpeedIcon from '@mui/icons-material/Speed';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';

const buildServices = [
  'Custom PC building with premium components',
  'Gaming PC optimization and assembly',
  'Workstation building for professionals',
  'Server assembly and configuration',
  'Performance testing and benchmarking',
  'Cable management and aesthetics',
  'Liquid cooling installation',
  'RGB lighting setup',
  'Overclocking services',
  'Component compatibility check'
];

const benefits = [
  {
    icon: PrecisionManufacturingIcon,
    title: 'Custom Design',
    description: 'Tailored builds based on your specific needs and preferences'
  },
  {
    icon: SpeedIcon,
    title: 'Performance Optimized',
    description: 'Every build is tested and optimized for maximum performance'
  },
  {
    icon: MemoryIcon,
    title: 'Premium Components',
    description: 'Only the highest quality, certified components used'
  },
  {
    icon: SettingsSuggestIcon,
    title: 'Expert Assembly',
    description: 'Built by certified technicians with years of experience'
  }
];

const specializations = [
  {
    title: 'Gaming Rigs',
    description: 'High-performance gaming computers with latest GPUs and processors'
  },
  {
    title: 'Workstations',
    description: 'Professional workstations for content creation and heavy workloads'
  },
  {
    title: 'Home Office',
    description: 'Efficient and reliable systems for work-from-home setups'
  },
  {
    title: 'Servers',
    description: 'Custom server builds for small businesses and enthusiasts'
  }
];

function DeviceBuilding() {
  return (
    <Box sx={{ py: 6, bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h1"
            className="gradient-text"
            sx={{ mb: 3 }}
          >
            Custom Device Building Services
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: 'text.secondary', mb: 4 }}
          >
            Build your dream device with our expert technicians
          </Typography>
          <Button
            component={RouterLink}
            to="/booking"
            variant="contained"
            size="large"
            sx={{
              py: 2,
              px: 4,
              fontSize: '1.2rem',
              background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #FF8E53 30%, #FE6B8B 90%)',
              }
            }}
          >
            Book Build Service
          </Button>
        </Box>

        {/* Benefits Section */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card className="glass-effect card-hover">
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box sx={{ mb: 2, color: 'primary.main' }}>
                    {React.createElement(benefit.icon, { sx: { fontSize: 40 } })}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Services List */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            mb: 8,
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
            Our Building Services
          </Typography>
          <Grid container spacing={2}>
            {buildServices.map((service, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={service} />
                  </ListItem>
                </List>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Specializations */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4,
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
            Our Specializations
          </Typography>
          <Grid container spacing={3}>
            {specializations.map((spec, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card 
                  className="glass-effect"
                  sx={{ 
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    p: 2
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    {spec.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {spec.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}

export default DeviceBuilding;
